export type JsonPrimitive = string | number | boolean | null;

export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export interface JsonObject {
  [key: string]: JsonValue;
}

export function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A record's own entry: keys come from user data, so inherited members (constructor, __proto__) never count. */
export function getOwn<T>(
  record: { readonly [key: string]: T },
  key: string,
): T | undefined {
  return Object.hasOwn(record, key) ? record[key] : undefined;
}

/** Sets an own entry; assigning `__proto__` would replace the prototype instead. */
export function setOwn<T>(
  record: { [key: string]: T },
  key: string,
  value: T,
): void {
  Object.defineProperty(record, key, {
    value,
    enumerable: true,
    writable: true,
    configurable: true,
  });
}

/** Stable key for a JSON value; object keys are sorted so equal values hash equally. */
export function stableStringify(value: JsonValue | undefined): string {
  if (value === undefined) {
    return "undefined";
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (isJsonObject(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(getOwn(value, k))}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

/** Structural JSON conversion for typed values (configs built from interfaces). */
export function toJsonValue(value: unknown): JsonValue {
  if (value == null) {
    return null;
  }
  if (typeof value === "string" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(toJsonValue);
  }
  if (typeof value === "object") {
    const out: JsonObject = {};
    for (const [k, v] of Object.entries(value)) {
      if (v !== undefined && typeof v !== "function") {
        setOwn(out, k, toJsonValue(v));
      }
    }
    return out;
  }
  return null;
}
